import type { Tool } from "@repo/app-types/entities/tool";
import type {
  GradingToolUsageAccuracy,
  ToolUsageAccuracy,
} from "@repo/app-types/entities/toolUsageAccuracy";
import type { FC } from "react";
import { useMemo } from "react";
import { GridList, GridListItem, type Key } from "react-aria-components";
import { ToolUsageAccuracyStatusIcon } from "./toolUsageAccuracyStatusIcon.js";

export interface ToolUsageAccuracyCardProps {
  data: GradingToolUsageAccuracy | null | undefined;
  /** Map of tool IDs to tool definitions. Used to display tool names. */
  tools: ReadonlyMap<string, Tool>;
  /** Called when a tool row is clicked. */
  onToolClick?: (toolId: string, toolName: string) => void;
}

/** Tool assessment with display-friendly properties. */
interface ToolRow extends ToolUsageAccuracy {
  tool_name: string;
}

export const ToolUsageAccuracyCard: FC<ToolUsageAccuracyCardProps> = (
  props,
) => {
  const { onToolClick } = props;

  const toolRows = useMemo(() => {
    if (!props.data) {
      return [];
    }

    const assessments: ToolRow[] = [];

    for (const [toolId, accuracy] of Object.entries(props.data?.tools ?? {})) {
      if (!accuracy) {
        continue;
      }

      const tool = props.tools.get(toolId);
      assessments.push({
        ...accuracy,
        tool_name: tool?.name ?? "Unknown tool",
      });
    }

    // Sort alphabetically by tool name
    assessments.sort((a, b) => a.tool_name.localeCompare(b.tool_name));

    return assessments;
  }, [props.data, props.tools]);

  const handleAction = (key: Key): void => {
    if (onToolClick === undefined) {
      return;
    }

    const toolId = typeof key === "string" ? key : undefined;
    const row = toolRows.find((r) => r.tool_id === toolId);

    if (toolId !== undefined && row !== undefined) {
      onToolClick(toolId, row.tool_name);
    }
  };

  if (toolRows.length === 0) {
    return (
      <div className="rounded-lg border border-border-light bg-white px-4 py-2 text-sm text-muted-foreground">
        No tool assessments.
      </div>
    );
  }

  return (
    <GridList
      aria-label="Tool usage accuracy"
      className="grid grid-cols-[auto_1fr_2fr] divide-y divide-border-light rounded-lg border border-border-light bg-white"
      items={toolRows}
      onAction={onToolClick !== undefined ? handleAction : undefined}
    >
      {(row) => (
        <GridListItem
          id={row.tool_id}
          className="col-span-full grid grid-cols-subgrid items-center gap-3 px-3 py-2 outline-hidden first:rounded-t-lg last:rounded-b-lg data-[react-aria-pressable]:cursor-pointer data-[react-aria-pressable]:hover:bg-blue-50"
          textValue={row.tool_name}
        >
          <ToolUsageAccuracyStatusIcon status={row.status} />

          <div className="text-sm">{row.tool_name}</div>

          <div className="text-sm">{row.summary}</div>
        </GridListItem>
      )}
    </GridList>
  );
};
